import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { CgFileRemove } from 'react-icons/cg';
import { IoDocumentTextOutline } from 'react-icons/io5';

import { getServiceId } from '@/api/tokens.helper.ts';
import { Divider, LoaderSpinner } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { DocumentPreviewModal } from '@/components/DocumentPreview/DocumentPreview';
import { Autocomplete, Input, TextArea } from '@/components/Inputs';
import { useTaskOptions } from '@/hooks/useTaskOptions';
import { DocumentType } from '@/store/documents/documents.dto.ts';
import {
  useAllDocumentsQuery,
  useDocumentByIdQuery,
  useUploadDocumentsFileMutation,
} from '@/store/documents/documents.store.ts';
import { useAllEquipmentQuery } from '@/store/equipment/equipment.store';
import type {
  CreateQuotePayload,
  Quote,
  QuoteStatus,
} from '@/store/expenses-store/quotes/quote.dto.ts';
import { useQuotesStore } from '@/store/expenses-store/quotes/quotes.store.ts';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store.ts';
import type { Person } from '@/store/persons/person.dto.ts';
import { usePerson } from '@/store/persons/person.store.ts';
import { useServiceProvidersQuery } from '@/store/service-providers/service-providers.store.ts';
import { compressImage } from '@/utils/compressImage';
import { extractAI } from '@/utils/extractAI.ts';
import { formatAmountDisplay, moneyInputProps, sanitizeAmount, toAmountString } from '@/utils/form';
import Modal from '@components/Modal/Modal.tsx';
import { ExpenseEquipmentField } from '@pages/Expenses/components/ExpenseEquipmentField.tsx';

import styles from './TransactionModal.module.scss';

type QuoteModalProps = {
  open: boolean;
  onClose: () => void;
  quote?: Quote | null;
  taskId?: string;
  initialDocumentId?: string;
  initialRecipientId?: string;
  counterpartName?: string;
  isExist?: boolean;
  onSuccess?: () => void;
};

const QuoteModal = ({
  open,
  onClose,
  quote,
  taskId: initialTaskId,
  initialDocumentId,
  initialRecipientId,
  counterpartName,
  isExist,
  onSuccess,
}: QuoteModalProps) => {
  const { removeQuote } = useQuotesStore();
  const hasExistingQuote = isExist ?? !!quote;
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isTemporarilyClosed, setIsTemporarilyClosed] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{
    contentType: string;
    name: string;
    storageLink: string;
  } | null>(null);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleDeleteClick = () => {
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (quote?.id) {
      await removeQuote(quote.id);
      handleClose();
      onSuccess?.();
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  const handleOpenPreview = (doc: { contentType: string; name: string; storageLink: string }) => {
    setIsTemporarilyClosed(true);
    setPreviewDocument(doc);
  };

  const handleClosePreview = () => {
    setPreviewDocument(null);
    setIsTemporarilyClosed(false);
  };

  // show the read-only view when we have a quote and the user is not editing
  const showForm = !hasExistingQuote || isEditing;

  return (
    <>
      <Modal
        open={open && !isDeleteConfirmOpen && !isTemporarilyClosed}
        onClose={handleClose}
        title={isEditing ? 'Edit Quote' : quote ? `Quote #${quote.quote_number}` : 'Add Quote'}
        haveActions={hasExistingQuote && !isEditing}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
      >
        {showForm ? (
          <NewQuote
            key={quote?.id ?? 'new'}
            quote={isEditing ? quote : undefined}
            taskId={initialTaskId}
            initialDocumentId={initialDocumentId}
            initialRecipientId={initialRecipientId}
            counterpartName={counterpartName}
            onClose={handleClose}
            onSuccess={onSuccess}
            isEditing={isEditing}
          />
        ) : (
          <ExistingQuote quote={quote} onPreviewDocument={handleOpenPreview} />
        )}
      </Modal>
      {previewDocument && (
        <DocumentPreviewModal document={previewDocument} onClose={handleClosePreview} />
      )}
      <ConfirmationModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Quote"
        message="Are you sure you want to delete this quote?"
      />
    </>
  );
};

export default QuoteModal;

type ExistingQuoteProps = {
  quote?: Quote | null;
  onPreviewDocument: (doc: { contentType: string; name: string; storageLink: string }) => void;
};

const ExistingQuote = ({ quote, onPreviewDocument }: ExistingQuoteProps) => {
  const { people, getPeople } = usePerson();
  // Local: the shared `tasksList` is what the tasks and maintenance lists render, and
  // `getAllTasks()` would replace their filtered cut with every task on the vessel.
  const tasks = useTaskOptions();
  const { data: quoteDocument } = useDocumentByIdQuery(quote?.document_id);

  useEffect(() => {
    if (!people) void getPeople();
  }, [people, getPeople]);

  const getPersonLabel = (person: Person) => {
    const first = person.person_info?.first_name?.trim() ?? '';
    const last = person.person_info?.last_name?.trim() ?? '';
    const fullName = `${first} ${last}`.trim();
    if (fullName) return fullName;
    const contact = person.contacts?.[0]?.contact?.trim();
    return contact || person.id;
  };

  const serviceProviderName = (people ?? []).find((p) => p.id === quote?.service_provider_id)
    ? getPersonLabel((people ?? []).find((p) => p.id === quote?.service_provider_id)!)
    : quote?.service_provider_id;

  const taskName = tasks.find((t) => t.id === quote?.task_id)?.title ?? quote?.task_id;
  // const categoryName = CATEGORY_OPTIONS.find((opt) => opt.id === quote?.equipments?.[0]?.id)?.title;
  const description = (quote?.detail as { description?: string })?.description;

  const handleDocumentClick = () => {
    if (quoteDocument) {
      onPreviewDocument({
        contentType: quoteDocument.contentType,
        name: quoteDocument.name,
        storageLink: quoteDocument.storageLink,
      });
    }
  };

  return (
    <div className={styles.existingTransactions}>
      <div className={styles.heading}>
        <p className={styles.date}>
          {quote?.date ? new Date(quote.date).toLocaleDateString('en-US') : '-'}
        </p>
        <p className={styles.amount}>
          {quote?.amount?.number} {quote?.amount?.currency}
        </p>
      </div>
      <div className={styles.recipientEquipment}>
        <div style={{ flex: 1 }}>
          <span>Service Provider</span>
          <span>{serviceProviderName ?? ''}</span>
          <Divider className={styles.modalDivider} />
        </div>
        {/*<div>*/}
        {/*  <span>Category</span>*/}
        {/*  <span>{categoryName ?? '-'}</span>*/}
        {/*  <Divider className={styles.modalDivider} />*/}
        {/*</div>*/}
      </div>
      <div className={styles.typeMethod}>
        <div>
          <span>Task</span>
          <span>{taskName ?? '-'}</span>
          <Divider className={styles.modalDivider} />
        </div>
        <div>
          <span>Quote Number</span>
          <span>{quote?.quote_number ?? '-'}</span>
          <Divider className={styles.modalDivider} />
        </div>
      </div>
      {!!quote?.equipments?.length && (
        <div className={styles.fieldGroup}>
          <span>Equipment</span>
          {quote.equipments.map((equipment) => (
            <span key={equipment.id}>{equipment.name}</span>
          ))}
          <Divider className={styles.modalDivider} />
        </div>
      )}
      {description && (
        <div className={styles.fieldGroup}>
          <span>Description</span>
          <span>{description}</span>
          <Divider className={styles.modalDivider} />
        </div>
      )}
      {quote?.document_id && (
        <div
          className={styles.invoiceCard}
          onClick={handleDocumentClick}
          style={{ cursor: quoteDocument ? 'pointer' : 'default' }}
        >
          <div>
            <p>{quoteDocument?.name ?? 'Document'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

type NewQuoteProps = {
  quote?: Quote | null;
  taskId?: string;
  initialDocumentId?: string;
  initialRecipientId?: string;
  counterpartName?: string;
  onClose: () => void;
  onSuccess?: () => void;
  isEditing?: boolean;
};

const NewQuote = ({
  quote,
  taskId: initialTaskId,
  initialDocumentId,
  initialRecipientId,
  counterpartName,
  onClose,
  onSuccess,
  isEditing,
}: NewQuoteProps) => {
  const { createQuote, updateQuote, loading } = useQuotesStore();
  const transactionCategories = useTransactionsStore((state) => state.transactionCategories);
  const getTransactionsCategories = useTransactionsStore(
    (state) => state.getTransactionsCategories,
  );
  // Same as above: a picker's task list is this form's own, not the page's behind it.
  const tasks = useTaskOptions();
  const { people, getPeople } = usePerson();
  const documentsQuery = useAllDocumentsQuery();
  const documents = (documentsQuery.data?.items ?? []).filter(
    (document) => document.type === DocumentType.FILE,
  );
  const { mutateAsync: uploadDocumentsFile } = useUploadDocumentsFileMutation();
  const documentFileInputRef = useRef<HTMLInputElement | null>(null);
  const extractFileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectErrors, setSelectErrors] = useState({
    amount: '',
    document: '',
    task: '',
    serviceProvider: '',
  });
  const [quoteNumber, setQuoteNumber] = useState(quote?.quote_number ?? '');
  const [date, setDate] = useState(quote?.date ?? '');
  const [amount, setAmount] = useState(sanitizeAmount(String(quote?.amount?.number ?? '')));
  const [currency, setCurrency] = useState(quote?.amount?.currency ?? 'USD');
  const [status, setStatus] = useState<QuoteStatus>((quote?.status as QuoteStatus) ?? 'revised');
  const [method, setMethod] = useState((quote?.detail as { method?: string })?.method ?? 'manual');
  const [description, setDescription] = useState(
    (quote?.detail as { description?: string })?.description ?? '',
  );
  const [recipient, setRecipient] = useState(
    quote?.service_provider_id ?? initialRecipientId ?? '',
  );
  const [recipientSearch, setRecipientSearch] = useState('');
  const [counterpartHint, setCounterpartHint] = useState<string | undefined>(counterpartName);
  const [categoryId, setCategoryId] = useState(() => {
    const d = quote?.detail as
      | { categoryId?: string; category?: string; category_id?: string }
      | undefined;
    return d?.categoryId ?? d?.category ?? d?.category_id ?? '';
  });
  const [categorySearch, setCategorySearch] = useState('');
  const [taskId, setTaskId] = useState(quote?.task_id ?? initialTaskId ?? '');
  const [equipmentId, setEquipmentId] = useState(quote?.equipments?.[0]?.id ?? '');
  const { data: equipmentList = [] } = useAllEquipmentQuery();
  const [taskSearch, setTaskSearch] = useState('');
  const [documentId, setDocumentId] = useState(quote?.document_id ?? initialDocumentId ?? '');
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentUploadId, setDocumentUploadId] = useState('');
  const [documentFileName, setDocumentFileName] = useState('');
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);
  const [extractFile, setExtractFile] = useState<File | null>(null);
  const [extractFileName, setExtractFileName] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);
  const vesselId = getServiceId();
  const serviceProvidersQuery = useServiceProvidersQuery(100, { vesselId, search: '' });
  const serviceProviders = serviceProvidersQuery.data?.pages?.flatMap((p) => p.items) ?? [];

  useEffect(() => {
    void getTransactionsCategories();
  }, [getTransactionsCategories]);

  useEffect(() => {
    if (!people) void getPeople();
  }, [people, getPeople]);

  useEffect(() => {
    setCounterpartHint(counterpartName);
  }, [counterpartName]);

  const getPersonLabel = (person: Person) => {
    const first = person.person_info?.first_name?.trim() ?? '';
    const last = person.person_info?.last_name?.trim() ?? '';
    const fullName = `${first} ${last}`.trim();
    if (fullName) return fullName;
    const contact = person.contacts?.[0]?.contact?.trim();
    return contact || person.id;
  };

  const resolvedRecipientLabel = (() => {
    if (!recipient) return undefined;
    const sp = serviceProviders.find((s) => s.id === recipient);
    if (sp) return sp.name;
    const person = (people ?? []).find((p) => p.id === recipient);
    if (person) return getPersonLabel(person);
    return undefined;
  })();

  // --- Document attachment (sent together with the form) ---
  const handleDocumentUploadClick = () => {
    documentFileInputRef.current?.click();
  };

  const parseCurrency = (value: string): string => {
    if (value === '$' || value === 'USD') return 'USD';
    if (value === '€' || value === 'EUR') return 'EUR';
    if (value === '£' || value === 'GBP') return 'GBP';
    return 'USD';
  };

  const handleDocumentFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const rawFile = event.target.files?.[0];
    event.target.value = '';
    if (!rawFile) return;
    setIsDocumentUploading(true);
    try {
      const file = await compressImage(rawFile);

      const response = await uploadDocumentsFile({ file, parentId: null });
      const responseData = response?.data as { id?: string } | { id?: string }[] | undefined;
      const uploadedId = Array.isArray(responseData) ? responseData[0]?.id : responseData?.id;
      setDocumentUploadId(uploadedId ?? '');
      setDocumentFileName(file.name);
      setDocumentId('');
      setDocumentSearch('');
      if (selectErrors.document) {
        setSelectErrors((prev) => ({ ...prev, document: '' }));
      }
    } catch (err) {
      console.error('[Quote] upload error:', err);
    } finally {
      setIsDocumentUploading(false);
    }
  };

  // --- AI extraction (only fills the form, file is not attached) ---
  const handleExtractUploadClick = () => {
    extractFileInputRef.current?.click();
  };

  const handleExtractFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawFile = event.target.files?.[0];
    event.target.value = '';
    if (!rawFile) return;
    setExtractFile(rawFile);
    setExtractFileName(rawFile.name);
  };

  const handleExtract = async () => {
    if (!extractFile) return;
    setIsExtracting(true);
    try {
      const file = await compressImage(extractFile);
      const formData = new FormData();
      formData.append('file', file);

      const result = await extractAI({ formData, type: 'quotation', method: 'extract' });

      if (result) {
        if (result.data.result.quote_number) setQuoteNumber(result.data.result.quote_number);
        if (result.data.result.date) setDate(result.data.result.date);
        if (result.data.result.total_amount)
          setAmount(sanitizeAmount(String(result.data.result.total_amount)));
        if (result.data.result.currency) setCurrency(parseCurrency(result.data.result.currency));
        if (result.data.result.description) setDescription(result.data.result.description);
        if (result.data.result.service_provider) {
          const foundSp = serviceProviders.find(
            (sp) => sp.name.toLowerCase() === result.data.result.service_provider.toLowerCase(),
          );
          if (foundSp) setRecipient(foundSp.id);
        }
        setExtractFile(null);
        setExtractFileName('');
      }
    } catch (err) {
      console.error('[Quote] extract error:', err);
    } finally {
      setIsExtracting(false);
    }
  };

  const resetForm = () => {
    setSelectErrors({ amount: '', document: '', task: '', serviceProvider: '' });
    setQuoteNumber('');
    setDate('');
    setAmount('');
    setCurrency('USD');
    setStatus('open');
    setMethod('manual');
    setDescription('');
    setRecipient('');
    setRecipientSearch('');
    setCategoryId('');
    setCategorySearch('');
    setTaskId('');
    setTaskSearch('');
    setEquipmentId('');
    setDocumentId('');
    setDocumentSearch('');
    setDocumentUploadId('');
    setDocumentFileName('');
    setExtractFile(null);
    setExtractFileName('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const nextSelectErrors = {
      amount: amount ? '' : 'Please enter an amount.',
      document: documentId || documentUploadId ? '' : 'Please choose a document.',
      task: taskId ? '' : 'Please choose a task.',
      serviceProvider: recipient || counterpartHint ? '' : 'Please choose a service provider.',
    };

    setSelectErrors(nextSelectErrors);

    const hasErrors = Object.values(nextSelectErrors).some(Boolean);
    if (hasErrors) {
      return;
    }

    const equipment = equipmentList.find(({ id }) => id === equipmentId);

    const payload: CreateQuotePayload = {
      quote_number: quoteNumber || Date.now().toString(),
      status,
      document_id: documentUploadId || documentId,
      amount: {
        number: toAmountString(amount),
        currency,
      },
      task_id: taskId,
      equipments: equipment ? [{ id: equipment.id, name: equipment.name }] : [],
      service_provider_id: recipient,
      attachments: [],
      detail: {
        method,
        description,
        categoryId,
      },
      date: date ? new Date(date).toISOString() : new Date().toISOString(),
    };

    if (isEditing && quote?.id) {
      await updateQuote(quote.id, payload);
    } else {
      await createQuote(payload);
    }
    resetForm();
    onClose();
    onSuccess?.();
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.uploadWrapper}>
        <div className={styles.uploadInfo}>
          <input
            ref={extractFileInputRef}
            type="file"
            accept="image/*,.pdf,application/pdf"
            className={styles.uploadInput}
            onChange={handleExtractFileChange}
          />
          {extractFileName ? (
            <span className={styles.uploadName}>{extractFileName}</span>
          ) : (
            <span className={styles.uploadLabel} onClick={handleExtractUploadClick}>
              Upload the document to extract quote info with AI
            </span>
          )}
        </div>
        <div className={styles.uploadActions}>
          {extractFile && (
            <button
              type="button"
              className={styles.extractButton}
              onClick={handleExtract}
              disabled={isExtracting}
            >
              {isExtracting ? <LoaderSpinner size={24} fullHeight={false} label="" /> : 'Extract'}
            </button>
          )}
          {extractFile ? (
            <button
              type="button"
              className={styles.uploadButton}
              onClick={(event) => {
                event.stopPropagation();
                setExtractFile(null);
                setExtractFileName('');
              }}
            >
              <CgFileRemove width={32} height={32} color={'var(--color-cold-white)'} />
            </button>
          ) : (
            <button
              onClick={handleExtractUploadClick}
              type="button"
              className={styles.uploadButton}
            >
              <IoDocumentTextOutline width={32} height={32} color={'var(--color-cold-white)'} />
            </button>
          )}
        </div>
      </div>
      <Input
        label="Number"
        type="text"
        placeholder="Quote number"
        value={quoteNumber}
        onChange={(e) => setQuoteNumber(e.target.value)}
      />
      <div className={styles.row}>
        <Input
          label="Amount"
          placeholder="0.00"
          suffix="$"
          {...moneyInputProps}
          value={formatAmountDisplay(amount)}
          onChange={(e) => {
            setAmount(sanitizeAmount(e.target.value));
            if (selectErrors.amount) {
              setSelectErrors((prev) => ({ ...prev, amount: '' }));
            }
          }}
          error={selectErrors.amount}
          mandatory
          required
        />
      </div>
      {initialTaskId ? (
        <Input
          label="Choose task"
          value={tasks.find((t) => t.id === initialTaskId)?.title ?? initialTaskId}
          onChange={() => {}}
          disabled
          mandatory
        />
      ) : (
        <Autocomplete
          label="Choose task"
          placeholder="Choose task"
          value={taskSearch}
          onChange={setTaskSearch}
          options={tasks
            .filter((task) => task.title.toLowerCase().includes(taskSearch.toLowerCase()))
            .map((task) => ({ label: task.title, value: task.id }))}
          onItemSelect={(id) => {
            setTaskId((id as string) ?? '');
            if (id) setTaskSearch('');
            if (id && selectErrors.task) {
              setSelectErrors((prev) => ({ ...prev, task: '' }));
            }
          }}
          selectedOptionLabel={tasks.find((t) => t.id === taskId)?.title}
          mandatory
          error={selectErrors.task}
        />
      )}
      <Autocomplete
        label="Service provider"
        placeholder="Search service provider"
        value={recipientSearch}
        onChange={setRecipientSearch}
        options={serviceProviders
          .filter((sp) => sp.name.toLowerCase().includes(recipientSearch.toLowerCase()))
          .map((sp) => ({ label: sp.name, value: sp.id }))}
        onItemSelect={(id) => {
          setRecipient((id as string) ?? '');
          setCounterpartHint(undefined);
          if (id) setRecipientSearch('');
          if (id && selectErrors.serviceProvider) {
            setSelectErrors((prev) => ({ ...prev, serviceProvider: '' }));
          }
        }}
        selectedOptionLabel={resolvedRecipientLabel ?? counterpartHint}
        mandatory
        error={selectErrors.serviceProvider}
      />
      <Autocomplete
        label="Category"
        placeholder="Search category"
        value={categorySearch}
        onChange={setCategorySearch}
        options={transactionCategories
          .filter(({ title }) => title.toLowerCase().includes(categorySearch.toLowerCase()))
          .map(({ id, title }) => ({ label: title, value: id }))}
        onItemSelect={(id) => {
          setCategoryId((id as string) ?? '');
          if (id) setCategorySearch('');
        }}
        selectedOptionLabel={
          transactionCategories.find(({ id, slug }) => id === categoryId || slug === categoryId)
            ?.title
        }
      />
      <ExpenseEquipmentField value={equipmentId} onChange={setEquipmentId} />
      <div className={styles.selectWithUpload}>
        <Autocomplete
          label="Document"
          placeholder="Search document"
          value={documentSearch}
          onChange={setDocumentSearch}
          options={documents
            .filter((doc) => doc.name.toLowerCase().includes(documentSearch.toLowerCase()))
            .map((doc) => ({ label: doc.name, value: doc.id }))}
          onItemSelect={(id) => {
            setDocumentId((id as string) ?? '');
            setDocumentUploadId('');
            setDocumentFileName('');
            if (id) setDocumentSearch('');
            if (selectErrors.document) {
              setSelectErrors((prev) => ({ ...prev, document: '' }));
            }
          }}
          selectedOptionLabel={
            documentFileName ? documentFileName : documents.find((d) => d.id === documentId)?.name
          }
          mandatory
          error={selectErrors.document}
        />
        <input
          ref={documentFileInputRef}
          type="file"
          className={styles.uploadInput}
          onChange={handleDocumentFileChange}
        />
        {/* {documentUploadId ? (
          <button
            type="button"
            className={styles.removeButton}
            onClick={() => {
              setDocumentUploadId('');
              setDocumentFileName('');
            }}
          >
            Remove
          </button>
        ) : ( */}
        <button onClick={handleDocumentUploadClick} type="button" className={styles.uploadButton}>
          {isDocumentUploading ? (
            <LoaderSpinner size={24} fullHeight={false} label="" />
          ) : (
            <IoDocumentTextOutline width={32} height={32} color={'var(--color-cold-white)'} />
          )}
        </button>
        {/* )} */}
      </div>
      <TextArea
        label="Description"
        placeholder="Annual engine service"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
      <div className={styles.buttons}>
        <ButtonOutlined type="button" onClick={onClose}>
          Cancel
        </ButtonOutlined>
        <Button type="submit" disabled={loading || isDocumentUploading || isExtracting}>
          {isDocumentUploading
            ? 'Uploading…'
            : isExtracting
              ? 'Extracting…'
              : loading
                ? 'Saving…'
                : isEditing
                  ? 'Update'
                  : 'Save'}
        </Button>
      </div>
    </form>
  );
};
